import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from "react-redux"
import {
  Button,
  styled,
  Grid,
  Box,
  Typography,
} from '@mui/material';

import * as _ from "lodash"

import { RootState, AppDispatch } from '../../state/store';
import {
  loginAsync,
  resetAuth,
  resetErrors,
  sendOtpAsync,
  setCurrentForm,
  setCurrentPage,
  setOtpVerificationStep,
  validateSessionAsync,
  verifyOtpAsync,
  setIsOtpVerified
} from '../../state/auth/authSlice';
import { fetchUserDetailsAsync } from '../../state/user/userSlice';
import { encryptData, isAdmin, isAssistant, isDoctor, maskedEmailUtil2, maskPhoneNoUtil, retrieveDataFromLocalStorage } from "../../utils"
import { IMAGE_PATHS } from '../../utils/imagePaths';
import { validateEmail } from '../../utils/validation';
import { AUTH_ERROR_CODES } from '../../constants/index';
import AuthFrame from "../../components/AuthFrame";
import theme from '../../constants/theme';
import TextInput from '../../components/TextField';
import FieldIconContainer from '../../components/FieldIconContainer';
import ErrorMsg from '../../components/ErrorMsg';
import ProgressLoader from '../../components/Loader';
import { IS_OTP_VERIFIED, REQ_LOCAL_KEYS, REQ_TOKENS_KEY_NAMES } from '../../constants';
import AlertBox from '../../components/AlertBox';
import SmsVerification from '../../components/SmsVerification';
import { HandleOtpVerifyPayloadType } from '../applications/Users/UpdateProfile/Profile';
import { CancelDeleteUserButton as LoginActioBtn } from '../../components/Button';
import Logo from '../../components/LogoSign';
import BackBtnComp from '../../components/BackBtn';
import ChooseOTPOption from '../../components/ChooseOTPOptions';
import { setShowAlert } from '../../state/utilts/utilsSlice';
import { TOAST_TYPE } from '../../utils/constantTypes';

const ForgotPasswordBtn = styled(Button)(({ theme }) => ({
  background: "none",
  cursor: "pointer",
  color: theme.bg.primaryDark,
  fontWeight: 'bold',
  ":hover": {
    textDecoration: "underline",
    background: "none",
  },
  ':focus': {
    textDecoration: "underline",
    background: "none",
  },
  padding: 0
}))

export const iconStyle = { color: theme.colors.primary, width: "100%" }
export const iconContainerStyle = { display: "flex", alignItems: "center", width: "20px", marginRight: "5px" }

enum OTP_TYPE_ENUM {
  SMS = 'SMS',
  EMAIL = 'EMAIL'
}

export interface OTPOption {
  id: string;
  isPrimary: boolean;
  label: string;
  description: string;
  value: string;
  type: OTP_TYPE_ENUM;
}

const Login = () => {
  const { loading: authLoading, error, tokens, otpToken, isOtpVerified } = useSelector((state: RootState) => state.auth);
  const {
    loading: userLoading,
    userData: {
      role,
      officeId,
      practiceLocationEnabled,
      subscription,
      phoneNumber: { countryCode, number }
    }
  } = useSelector((state: RootState) => state.user);
  const { show, message, type } = useSelector((state: RootState) => state.utils);
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false);
  const localTokens = retrieveDataFromLocalStorage(REQ_TOKENS_KEY_NAMES);
  const localIsOtpVerified = retrieveDataFromLocalStorage(IS_OTP_VERIFIED);
  const localRole = JSON.parse(retrieveDataFromLocalStorage(REQ_LOCAL_KEYS));
  const [hasLoggedIn, setHasLoggedIn] = useState(false);
  const [otpOptions, setOtpOptions] = useState<OTPOption[]>([]);
  const [selectedOTPOption, setSelectedOTPOption] = useState<OTPOption | null>(null);
  const [rememberDevice, setRememberDevice] = useState(false);
  const subscriptionHasExpired = subscription?.hasExpired;
  const hasActiveSubscription = !!subscription?._id;

  const canUserAccessGPS = ({
    role: userRole,
    officeId: userOfficeId,
    practiceLocationEnabled: userPracticeLocationEnabled,
    hasExpired,
    hasSubscription
  }: {
    role: string;
    officeId: string | null;
    practiceLocationEnabled: boolean | null;
    hasExpired: boolean | null | undefined;
    hasSubscription: boolean;
  }) => {
    // Align with SidebarMenu GPS tab: need active sub, not just !hasExpired
    // (!hasExpired is true when no subscription — hasExpired defaults false)
    return (
      !!userOfficeId &&
      userPracticeLocationEnabled === true &&
      !!hasSubscription &&
      !hasExpired &&
      (isDoctor(userRole) || isAssistant(userRole))
    );
  };

  useEffect(() => {
    dispatch(resetErrors())
    // // if user lands on login during active session
    if (hasActiveSession && hasOtpVerified) {
      (async () => {
        const res = await dispatch(validateSessionAsync({ ...localTokens }))
        const isAuthenticated = _.get(res, "payload.isAuthenticated", null);
        if (
          isAuthenticated &&
          canUserAccessGPS({
            role: role || localRole,
            officeId,
            practiceLocationEnabled,
            hasExpired: subscriptionHasExpired,
            hasSubscription: hasActiveSubscription
          })
        ) {
          navigate("/gps");
        } else if (isAuthenticated && isAdmin(role || localRole)) {
          navigate("/dashboard")
        }
        else if (isAuthenticated) {
          navigate("/accounts/profile")
        }
      })()
      return;
    }
    // // if user lands on login/refresh on otp verify page on login
    else if (hasActiveSession && !hasOtpVerified) {
      localStorage.clear();
      setHasLoggedIn(false);
    }
  }, []);

  const hasActiveSession = (({ localTokens, tokens }) => {
    const storeHasTokens = REQ_TOKENS_KEY_NAMES.every(token => tokens[token]);
    const localStorageHasTokens = REQ_TOKENS_KEY_NAMES.every(token => localTokens[token]);
    return storeHasTokens || localStorageHasTokens
  })({ localTokens, tokens, localIsOtpVerified, isOtpVerified });

  const hasOtpVerified = (({ localIsOtpVerified, isOtpVerified }) => {
    return isOtpVerified || localIsOtpVerified
  })({ localIsOtpVerified, isOtpVerified });

  const handleForgotPassword = () => {
    navigate('/forgot-password')
  };

  const formFields = [
    {
      labelImageName: "sms",
      label: "Username",
      placeholder: "Email",
      testID: 'email-input',
      id: 'username',
      value: username,
      autoComplete: 'username',
      validateError: usernameError ? true : false,
      validateErrormsg: usernameError,
      onChangeText: (text: string) => {
        dispatch(resetErrors());
        validate(text, "email");
      },
      icon: <FieldIconContainer imgSrc={IMAGE_PATHS.PROFILE} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
      type: 'email',
      handleOnFocus: () => dispatch(resetErrors())
    },
    {
      labelImageName: "lock",
      label: "Password",
      labelStyles: { marginTop: 28, marginBottom: 8 },
      placeholder: "Password",
      testID: 'password-input',
      accessibilityLabel: 'password-input',
      id: 'password',
      value: password,
      autoComplete: 'current-password',
      passwordVisible,
      iconName: passwordVisible ? "eye-off" : "eye-outline",
      validateErrormsg: passwordError,
      validateError: passwordError ? true : false,
      onChangeText: (text: string) => {
        if (passwordError) setPasswordError('');
        dispatch(resetErrors());
        setPassword(text)
      },
      onIconClick: () => {
        setPasswordVisible((prev) => !prev);
      },
      type: 'password',
      icon: <FieldIconContainer imgSrc={IMAGE_PATHS.PASSWORD} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
      inputProps: true,
      handleOnFocus: () => {
        setPasswordError('');
        dispatch(resetErrors())
      }
    }];

  const validate = (text: string, type: string) => {
    switch (type) {
      case "email":
        setUsername(text);
        let emailError = validateEmail(text);
        setUsernameError(emailError);
        break;
    }
  };

  // // common method to call send otp
  const handleSendOtp = async ({ accessToken, otpMedium }) => {
    const payload: any = {};
    payload.type = otpMedium.type;
    payload.isPrimary = otpMedium.isPrimary;
    if (otpMedium.type === OTP_TYPE_ENUM.SMS) {
      payload.phoneNumber = otpMedium.value;
    } else if (otpMedium.type === OTP_TYPE_ENUM.EMAIL) {
      payload.userEmail = otpMedium.value;
    }
    await dispatch(
      sendOtpAsync({
        accessToken,
        data: {
          ...payload,
        }
      })
    );
  };

  const handleLogin = async () => {
    if (!password.length) {
      setPasswordError("Password is required.");
    }
    if (!username.length) {
      setUsernameError("Username is required.");
    }
    if (!usernameError && !passwordError && username && password) {
      const _loginFrom = encryptData(process.env.REACT_APP_LOGIN_FROM);
      try {
        const loginRes = await dispatch(
          loginAsync({ username, password, loginFrom: _loginFrom })
        );
        if (
          loginRes.meta.requestStatus === 'rejected' &&
          _.get(loginRes, 'payload.code') ===
            AUTH_ERROR_CODES.PRACTICE_INCOMPLETE
        ) {
          dispatch(
            setShowAlert({
              show: true,
              message: 'Complete your location details to finish login setup.',
              type: TOAST_TYPE.ERROR
            })
          );
          navigate('/signUp');
          return;
        }
        const accessToken =
          _.get(loginRes, 'payload.tokens.accessToken') ||
          _.get(loginRes, 'payload.accessToken');
        const MFARequired = _.get(
          loginRes,
          'payload.MFARequired',
          false
        );
        if (accessToken) {
          setPassword('');
          setPasswordVisible(false);
          const userDetailsRes: any = await dispatch(fetchUserDetailsAsync({ accessToken }));
          const officeId = _.get(userDetailsRes, 'payload.officeId');
          const role = _.get(userDetailsRes, 'payload.role');
          const phoneNumber = _.get(userDetailsRes, 'payload.phoneNumber');
          const countryCode = _.get(phoneNumber, 'countryCode', false);
          const number = _.get(phoneNumber, 'number', false);
          const practiceLocationEnabled = _.get(
            userDetailsRes,
            'payload.practiceLocationEnabled',
            null
          );
          const hasExpired = _.get(
            userDetailsRes,
            'payload.subscription.hasExpired',
            null
          );
          const hasSubscription = !!_.get(
            userDetailsRes,
            'payload.subscription._id',
            null
          );
          const email = _.get(userDetailsRes, 'payload.email', []);
          const secondaryEmail = _.get(
            userDetailsRes,
            'payload.additionalEmails',
            []
          );
          const secondaryPhoneNo = _.get(
            userDetailsRes,
            'payload.additionalPhoneNumber',
            []
          );
          if (email) {
            if (!MFARequired) {
              dispatch(setIsOtpVerified(true));
              const canAccessGPS = canUserAccessGPS({
                role,
                officeId,
                practiceLocationEnabled,
                hasExpired,
                hasSubscription
              });
              if (canAccessGPS) {
                navigate('/gps');
              } else if (officeId && isAdmin(role)) {
                navigate('/dashboard');
              } else {
                navigate('/accounts/profile');
              }
              return;
            }
            const otpOptions = [
              {
                id: 'sms-primary',
                isPrimary: true,
                label: 'Primary Phone Number',
                description: maskPhoneNoUtil(`${countryCode}${number}`),
                value: `${countryCode}${number}`,
                type: OTP_TYPE_ENUM.SMS
              },
              {
                id: 'email-primary',
                isPrimary: true,
                label: 'Primary Email',
                description: maskedEmailUtil2(email),
                value: email,
                type: OTP_TYPE_ENUM.EMAIL
              }
            ];
            if (secondaryPhoneNo?.length) {
              otpOptions.push({
                id: 'sms-secondary',
                isPrimary: false,
                label: 'Secondary Phone Number',
                description: maskPhoneNoUtil(secondaryPhoneNo[0]),
                value: secondaryPhoneNo[0],
                type: OTP_TYPE_ENUM.SMS
              });
            }
            if (secondaryEmail?.length) {
              otpOptions.push({
                id: 'email-secondary',
                isPrimary: false,
                label: 'Secondary Email',
                description: maskedEmailUtil2(secondaryEmail[0]),
                value: secondaryEmail[0],
                type: OTP_TYPE_ENUM.EMAIL
              });
            }
            // If Admin, skip OTP option selection and always use primary phone
            if (isAdmin(role)) {
              const primarySms = otpOptions?.find((o) => o.id === 'sms-primary' && o.value);
              if (primarySms) {
                dispatch(setOtpVerificationStep({ isEmailOptVerified: true }));
                setSelectedOTPOption(primarySms);
                setOtpOptions([]);
                setHasLoggedIn(true);
                await handleSendOtp({ accessToken, otpMedium: primarySms });
              } else {
                // Rare fallback: if no primary phone, show options
                setOtpOptions(otpOptions);
              }
            } else {
              setOtpOptions(otpOptions);
            }
          }
        }
      } catch (err) {
        console.log('Error on login', err);
      }
    }
  };
  const naviagteToSignUp = () => {
    setUsernameError('');
    setPasswordError('');
    dispatch(resetErrors());
    dispatch(setCurrentForm('signup'));
    dispatch(setCurrentPage('signup'));
    navigate('/signup');
  };

  const isLoginBtnDisabled = (userLoading || authLoading);

  const handleHasLoggedIn = () => {
    setHasLoggedIn(prev => !prev);
  };

  const handleOtpVerify = async (payload: HandleOtpVerifyPayloadType) => {
    try {
      const res = await dispatch(
        verifyOtpAsync({
          accessToken: tokens.accessToken,
          data: {
            otpToken,
            otp: payload.otp,
            username,
            mobileNo: `${countryCode}${number}`,
            rememberDevice
          }
        })
      );

      const err = _.get(res, 'error');
      if (!err) {
        dispatch(setOtpVerificationStep({ isEmailOptVerified: false }));
        const canAccessGPS = canUserAccessGPS({
          role,
          officeId,
          practiceLocationEnabled,
          hasExpired: subscriptionHasExpired,
          hasSubscription: hasActiveSubscription
        });
        if (canAccessGPS) {
          navigate('/gps');
        } else if (officeId && isAdmin(role)) {
          navigate('/dashboard');
        } else {
          navigate('/accounts/profile');
        }
      }
    }
    catch (err) {

    }
  };


  const handleOtpResend = async () => {
    try {
      const res = await handleSendOtp({
        accessToken: tokens.accessToken,
        otpMedium: selectedOTPOption
      });
    } catch (err) {}
  };

  const handleCloseOtpVerify = async () => {
    localStorage.clear();
    await dispatch(resetAuth());
    handleHasLoggedIn();
  };

  const handleOtpOptionContinue = async (id) => {
    const option = otpOptions?.filter((option) => option.id === id);
    if (option?.length) {
      setOtpOptions([]);
    }
    const otpMedium = option[0];
    if (otpMedium?.type === 'SMS') {
      dispatch(setOtpVerificationStep({ isEmailOptVerified: true }));
    }
    setSelectedOTPOption(otpMedium);
    handleHasLoggedIn();
    await handleSendOtp({
      accessToken: tokens.accessToken,
      otpMedium
    });
  };

  const handleBackToLogin = async() => {
    setOtpOptions([]);
    localStorage.clear();
    await dispatch(resetAuth());
  };

  return (
    <>
      <ProgressLoader loading={userLoading || authLoading} />
      <AlertBox type={type} showAlert={show} message={message} />
      {/* either user is fresh/has refreshed on otp screen */}
      {(!hasActiveSession &&
        !hasLoggedIn &&
        !hasOtpVerified &&
        !otpOptions.length) ||
      (!hasLoggedIn && !hasOtpVerified && !otpOptions.length) ? (
        <AuthFrame title={'Login to DentScribe Web Portal'}>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!isLoginBtnDisabled) handleLogin();
            }}
          >
            <Grid container>
            <Grid item xs={12} gap={1}>
              {formFields.slice(0, 3).map((textInput) => (
                <TextInput key={textInput.label} {...textInput} />
              ))}
            </Grid>
            <Grid item xs={12} gap={1}>
              {error ? <ErrorMsg error={error} /> : null}
            </Grid>
            <Grid container justifyContent="flex-end" item xs={12} gap={1}>
              <ForgotPasswordBtn onClick={handleForgotPassword}>
                Forgot Password?
              </ForgotPasswordBtn>
            </Grid>
            <Grid item xs={12} gap={1}>
              <Box display={'flex'} flexDirection={'column'} gap={2} py={2}>
                <LoginActioBtn
                  sx={{ background: theme.gradient.primary, borderRadius: 10 }}
                  variant="contained"
                  fullWidth
                  type="submit"
                  onClick={handleLogin}
                  disabled={isLoginBtnDisabled}
                >
                  Login
                </LoginActioBtn>
                <LoginActioBtn
                  sx={{ background: theme.gradient.primary, borderRadius: 5 }}
                  variant="contained"
                  fullWidth
                  type="button"
                  onClick={naviagteToSignUp}
                  disabled={isLoginBtnDisabled}
                >
                  Sign Up
                </LoginActioBtn>
              </Box>
            </Grid>
            </Grid>
          </Box>
        </AuthFrame>
      ) : otpOptions?.length ? (
        <ChooseOTPOption
          options={otpOptions}
          handleContinue={handleOtpOptionContinue}
          handleBackToLogin={handleBackToLogin}
        />
      ) : hasActiveSession && hasLoggedIn && !hasOtpVerified ? (
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 10px',
              padding: 3,
              borderRadius: 2,
              maxWidth: 400
            }}
          >
            <BackBtnComp handleOnClick={handleCloseOtpVerify} />
            <Box mb={4}>
              <Logo light clickable={false} />
            </Box>
            <Box>
              <SmsVerification
                handleOtpVerify={handleOtpVerify}
                handleOtpResend={handleOtpResend}
                email={selectedOTPOption.value}
                mobileNo={selectedOTPOption.value}
                showRememberDevice
                rememberDevice={rememberDevice}
                onRememberDeviceChange={setRememberDevice}
              />
            </Box>
          </Box>
        </Box>
      ) : null}
    </>
  );
};

export default Login;
